import { ISalesLine } from '@msdyn365-commerce-modules/order-management';
import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { SalesOrder } from '@msdyn365-commerce/retail-proxy';
import { useEffect, useRef } from 'react';
import { useMappingService, useTrackingService } from './serviceHooks';

/*
    Relewise does not dedupe cart events for B2C customers.
    We can only reliably fire this event upon order confirmation page load,
    but doing that can lead to duplicated events if the user reloads the page.

    This solution is not 100% foolproof, as it relies on localstorage and users can always go back to old order confirmation links.
*/
const STORAGE_KEY = 'relewise_tracked_orders';
const MAX_STORED_ORDERS = 50;

const getTrackedOrders = (): string[] => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
};
const storeTrackedOrder = (orderNumber: string) => {
    const orders = getTrackedOrders();
    if (orders.includes(orderNumber)) {
        return;
    }

    orders.push(orderNumber);

    if (orders.length > MAX_STORED_ORDERS) {
        orders.shift();
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(orders));
};
const hasTrackedOrder = (orderNumber: string) => {
    return getTrackedOrders().includes(orderNumber);
};

export const useOrderTracker = (salesLines: ISalesLine[] | undefined, salesOrder: SalesOrder | undefined, request: IRequestContext) => {
    const hasTrackedOrderRef = useRef(false);
    const relewiseTrackingService = useTrackingService(request);
    const relewiseMappingService = useMappingService(request);

    useEffect(() => {
        if (!salesLines?.length || !salesOrder || hasTrackedOrderRef.current) {
            return;
        }
        const orderNumber = salesOrder.Id;
        if (hasTrackedOrder(orderNumber)) {
            return;
        }

        const relewiseOrder = relewiseMappingService.mapToRelewiseOrder(salesLines, salesOrder);
        relewiseTrackingService.trackOrder(relewiseOrder);
        storeTrackedOrder(orderNumber);
        hasTrackedOrderRef.current = true;
    }, [salesOrder, salesLines, relewiseTrackingService, relewiseMappingService]);
};
